'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { SerwistProvider, useSerwist } from '@serwist/next/react';

/** @deprecated Unscoped key — migrate to {@link lastOrgStorageKey}. */
export const LAST_ORG_STORAGE_KEY_LEGACY = 'lqd:last-org';

export function lastOrgStorageKey(userId: string): string {
  return `lqd:last-org:${userId}`;
}

export function getLastOrgId(userId: string | null | undefined): string | null {
  if (!userId) return null;
  try {
    const scoped = localStorage.getItem(lastOrgStorageKey(userId));
    if (scoped) return scoped;

    const legacy = localStorage.getItem(LAST_ORG_STORAGE_KEY_LEGACY);
    if (legacy) {
      localStorage.setItem(lastOrgStorageKey(userId), legacy);
      localStorage.removeItem(LAST_ORG_STORAGE_KEY_LEGACY);
      return legacy;
    }
  } catch {
    // Private browsing / quota — non-fatal.
  }
  return null;
}

export function setLastOrgId(userId: string, organizationId: string): void {
  try {
    localStorage.setItem(lastOrgStorageKey(userId), organizationId);
    localStorage.removeItem(LAST_ORG_STORAGE_KEY_LEGACY);
  } catch {
    // Private browsing / quota — non-fatal.
  }
}

export type PwaState = {
  /** Running as an installed PWA (standalone display mode). */
  installed: boolean;
  /** `beforeinstallprompt` fired and install is available. */
  installable: boolean;
  /** Browser online flag (navigator.onLine). */
  online: boolean;
  /** Browser-reported constrained connection (2G or data saver). */
  slowConnection: boolean;
  /** A new service worker is waiting to activate. */
  updateAvailable: boolean;
  /** Trigger the native install prompt. Resolves false if dismissed/unavailable. */
  promptInstall: () => Promise<boolean>;
  /** Skip waiting and reload into the new service worker. */
  applyUpdate: () => void;
};

const PwaContext = createContext<PwaState | null>(null);

function PwaStateProvider({ children }: { children: ReactNode }) {
  const { serwist } = useSerwist();
  const [installed, setInstalled] = useState(false);
  const [installable, setInstallable] = useState(false);
  const [online, setOnline] = useState(true);
  const [slowConnection, setSlowConnection] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    const syncInstalled = () => {
      setInstalled(
        mq.matches ||
          ('standalone' in navigator &&
            (navigator as Navigator & { standalone?: boolean }).standalone ===
              true)
      );
    };
    syncInstalled();
    mq.addEventListener('change', syncInstalled);

    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    setOnline(navigator.onLine);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    const connection = (
      navigator as Navigator & { connection?: NetworkInformation }
    ).connection;
    const syncConnection = () => {
      setSlowConnection(
        Boolean(
          connection?.saveData ||
          connection?.effectiveType === 'slow-2g' ||
          connection?.effectiveType === '2g'
        )
      );
    };
    syncConnection();
    connection?.addEventListener('change', syncConnection);

    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      deferredPromptRef.current = event as BeforeInstallPromptEvent;
      setInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setInstallable(false);
      deferredPromptRef.current = null;
      setInstalled(true);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      mq.removeEventListener('change', syncInstalled);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      connection?.removeEventListener('change', syncConnection);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!serwist) return;

    const onWaiting = () => {
      // First install has no controller — activate immediately without a banner.
      if (!navigator.serviceWorker.controller) {
        void serwist.messageSkipWaiting();
        return;
      }
      setUpdateAvailable(true);
    };
    serwist.addEventListener('waiting', onWaiting);

    return () => {
      serwist.removeEventListener('waiting', onWaiting);
    };
  }, [serwist]);

  const promptInstall = useCallback(async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return false;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    deferredPromptRef.current = null;
    setInstallable(false);
    return outcome === 'accepted';
  }, []);

  const applyUpdate = useCallback(() => {
    if (!serwist) return;

    const onControllerChange = () => {
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange,
      { once: true }
    );
    void serwist.messageSkipWaiting();
  }, [serwist]);

  return (
    <PwaContext.Provider
      value={{
        installed,
        installable,
        online,
        slowConnection,
        updateAvailable,
        promptInstall,
        applyUpdate
      }}
    >
      {children}
    </PwaContext.Provider>
  );
}

/**
 * PWA shell: registers Serwist in production and exposes `usePwa()` so UI
 * never talks to the service worker directly.
 */
export function PwaProvider({ children }: { children: ReactNode }) {
  const disableSw = process.env.NODE_ENV === 'development';

  return (
    <SerwistProvider
      swUrl="/sw.js"
      disable={disableSw}
      register={!disableSw}
      reloadOnOnline
    >
      <PwaStateProvider>{children}</PwaStateProvider>
    </SerwistProvider>
  );
}

export function usePwa(): PwaState {
  const ctx = useContext(PwaContext);
  if (!ctx) {
    throw new Error('usePwa must be used within PwaProvider');
  }
  return ctx;
}

// Minimal typing for Chromium's install prompt event.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface NetworkInformation extends EventTarget {
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
  saveData?: boolean;
}
